import { DayPicker, type DayButtonProps } from '@daypicker/react';
import { addDays, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DayNote } from './DatePick';
import { dayLocale, toIso, type IsoDate } from './dates';
import { Popover } from './Popover';

export interface DayStepProps {
  /** What the days are, for a screen reader: `Days in Tokyo`. */
  label: string;
  /** The first and last day that may be picked: a stay's. */
  first: IsoDate;
  last: IsoDate;
  value: IsoDate;
  onChange: (day: IsoDate) => void;
  /** What a day holds, under its number in the calendar: a dot for a day planned. */
  note?: ((day: IsoDate) => DayNote | null) | undefined;
}

/** A day's button in the calendar, its note under its number. */
function NotedDay({
  day,
  modifiers,
  note,
  ...button
}: DayButtonProps & { note: (day: IsoDate) => DayNote | null }) {
  const said = note(toIso(day.date));
  return (
    <button
      ref={(element) => {
        if (element && modifiers.focused) element.focus();
      }}
      {...button}
      title={said?.said}
      aria-label={said ? `${String(button['aria-label'])}, ${said.said}` : button['aria-label']}
    >
      {button.children}
      {said && (
        <span className="rdp-note" aria-hidden="true">
          {said.mark}
        </span>
      )}
    </button>
  );
}

const NO_NOTE = () => null;

/**
 * One day of a stay, walked as a calendar's days are: the day before and after by its arrows, any
 * by its calendar — opened from the day said, only the stay's days to pick, each one planned
 * marked. However long the stay, it takes one line.
 */
export function DayStep({ label, first, last, value, onChange, note = NO_NOTE }: DayStepProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const shift = (by: number) => toIso(addDays(parseISO(value), by));
  const said = new Intl.DateTimeFormat(i18n.language === 'et' ? 'et-EE' : 'en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(Date.parse(`${value}T00:00:00Z`));
  const today = note(value);
  const noted = useMemo(
    () => ({
      className: 'rdp-noted',
      components: {
        DayButton: (props: DayButtonProps) => <NotedDay {...props} note={note} />,
      },
    }),
    [note],
  );
  // An arrow to a day there is; at the stay's end, one that goes nowhere.
  const arrow = (name: string, glyph: string, to: IsoDate | null) =>
    to === null ? (
      <button
        type="button"
        className="icon-button icon-button-small"
        aria-label={name}
        disabled
      >
        {glyph}
      </button>
    ) : (
      <button
        type="button"
        className="icon-button icon-button-small"
        aria-label={name}
        title={name}
        onClick={() => {
          onChange(to);
        }}
      >
        {glyph}
      </button>
    );
  return (
    <div className="day-step" role="group" aria-label={label}>
      {arrow(t('date.before'), '‹', value > first ? shift(-1) : null)}
      <Popover
        open={open}
        onOpenChange={setOpen}
        label={label}
        trigger={(props) => (
          <button type="button" className="day-step-day" {...props}>
            <span>{said}</span>
            {today && <span className="day-step-note">{today.said}</span>}
          </button>
        )}
      >
        <DayPicker
          mode="single"
          required
          selected={parseISO(value)}
          defaultMonth={parseISO(value)}
          weekStartsOn={1}
          disabled={[{ before: parseISO(first) }, { after: parseISO(last) }]}
          locale={dayLocale(i18n.language)}
          {...noted}
          autoFocus
          onSelect={(picked) => {
            onChange(toIso(picked));
            setOpen(false);
          }}
        />
      </Popover>
      {arrow(t('date.after'), '›', value < last ? shift(1) : null)}
    </div>
  );
}
